import { types } from '@sunwisesoftware/sunwise-template-core';
import i18next from 'i18next';
import { isEmpty } from 'lodash';

import { URL_TYPES } from 'common/constants';
import {
    downloadFile,
    getFileExtensionByPath,
    handleDownloadSunwiseFile,
} from 'common/utils/helpers';

import { resolveUrlType } from '../afterSalesConsumptionHistory/helpers';

import {
    CSV_FILE,
    ELECTRIC_BILL_TYPE,
    PDF_LOW_QUALITY,
    PDF_MEDIUM_QUALITY,
    PDF_REAL_TEXT,
} from './constants';

export const getDownloadOptions = (selectedDocument) => {
    const { format, type, urlType } = selectedDocument || {};
    let options = [];

    const csvFile = { label: `CSV - ${i18next.t('File')}`, value: CSV_FILE };

    const electricBillType = {
        label: `${i18next.t('Image').toUpperCase()} - ${i18next.t(
            'Medium quality',
        )}`,
        value: PDF_MEDIUM_QUALITY,
    };

    const pdfLowQuality = {
        label: `PDF - ${i18next.t('Low quality')}`,
        value: PDF_LOW_QUALITY,
    };

    const pdfMediumQuality = {
        label: `PDF - ${i18next.t('Medium quality')}`,
        value: PDF_MEDIUM_QUALITY,
    };

    const pdfRealText = { label: 'PDF', value: PDF_REAL_TEXT };

    switch (type) {
        case ELECTRIC_BILL_TYPE:
            options =
                urlType === URL_TYPES.WISE && format === 'png'
                    ? [{ ...electricBillType, disabled: true }]
                    : [{ ...pdfMediumQuality, disabled: true }];
            break;

        case types.REPORT_TYPE:
            options = [pdfRealText, pdfLowQuality, pdfMediumQuality, csvFile];
            break;

        default:
            options = [{ ...pdfMediumQuality, disabled: true }];
            break;
    }

    return [
        { disabled: true, label: i18next.t('Select'), value: '' },
        ...options,
    ];
};

export const getIsDownloadingFileContextMessage = ({
    isFetchingPdf,
    loadingContext,
}) => {
    if (isEmpty(loadingContext)) return;
    else if (isFetchingPdf)
        loadingContext.openLoading(i18next.t('Generating PDF').concat('...'));
    else loadingContext.closeLoading();
};

export const getIsLoadingContextMessage = ({
    isFetching,
    isFetchingCustomTemplate,
    isInitializing,
    loadingContext,
}) => {
    if (isEmpty(loadingContext)) return;
    if (isFetching || isFetchingCustomTemplate || isInitializing)
        loadingContext.openLoading(i18next.t('Loading').concat('...'));
    else loadingContext.closeLoading();
};

export const getIsUpdatingContextMessage = (
    editionDisabled,
    isUpdatingCustomTemplate,
    loadingContext,
    setEditionDisabled,
) => {
    if (isEmpty(loadingContext)) return;
    if (isUpdatingCustomTemplate)
        loadingContext.openLoading(i18next.t('Saving').concat('...'));
    else {
        if (!editionDisabled) setEditionDisabled(true);
        loadingContext.closeLoading();
    }
};

export const handleCloseDrawer = (swipeableDrawerRef) => {
    if (swipeableDrawerRef.current) {
        const { handleOpen } = swipeableDrawerRef.current;
        handleOpen(false);
    }
};

export const handleClickDocumentItemBuild = ({
    document,
    fetchCustomTemplate,
    initTemplate,
    resetTemplate,
    selectedDocument,
    setDownloadFormat,
    setSelectedDocument,
    swipeableDrawerRef,
}) => {
    const { id } = document;
    if (id !== selectedDocument.id) {
        resetTemplate();
        setSelectedDocument(document);
        setDownloadFormat(PDF_MEDIUM_QUALITY);
        fetchCustomTemplate({ id, initTemplate });
    }
    handleCloseDrawer(swipeableDrawerRef);
};

export const handleClickDownloadBuild = ({
    downloadFormat,
    downloadReportCsv,
    downloadReportPdf,
    selectedDocument,
}) => {
    const { archive, id, name, type } = selectedDocument;
    switch (type) {
        case ELECTRIC_BILL_TYPE:
            if (getFileExtensionByPath(selectedDocument?.archive) === 'xml')
                return handleDownloadSunwiseFile(archive, name);
            return downloadFile(archive, name);
        default:
            switch (downloadFormat) {
                case CSV_FILE:
                    return downloadReportCsv({ id, name });
                default:
                    return downloadReportPdf({
                        downloadFormat,
                        id,
                        name,
                        type,
                    });
            }
    }
};

export const handleClickElectricBillBuild = ({
    document,
    fetchWiseFile,
    fetchZapFile,
    loadingContext,
    resetTemplate,
    setDownloadFormat,
    setSelectedDocument,
    swipeableDrawerRef,
}) => {
    resetTemplate();

    const urlType = resolveUrlType(document.uploadOrigin, document.urlType);

    switch (urlType) {
        case URL_TYPES.WISE:
            loadingContext.openLoading(i18next.t('Getting file').concat('...'));

            fetchWiseFile(document?.fileName, async (data) => {
                let archive = data.url;
                const format = getFileExtensionByPath(data.url);

                setSelectedDocument({ ...document, archive, format });
                loadingContext.closeLoading();
            });
            break;
        case URL_TYPES.ZAP:
            loadingContext.openLoading(i18next.t('Getting file').concat('...'));

            fetchZapFile(document?.fileName, async (data) => {
                let archive = data.url;
                const format = getFileExtensionByPath(data.url);
                setSelectedDocument({ ...document, archive, format });

                loadingContext.closeLoading();
            });
            break;
        default:
            setSelectedDocument(document);
    }

    setDownloadFormat(PDF_MEDIUM_QUALITY);
    handleCloseDrawer(swipeableDrawerRef);
};

export const handleScrollTopSideBar = (sideBarRef) => {
    if (sideBarRef && sideBarRef.current) sideBarRef.current.scrollTop = 0;
};
